'use client';

import { forwardRef, type ReactNode } from 'react';

import { Button, type ButtonProps as BaseButtonProps } from '../../primitives/button';
import { Spinner } from '../../primitives/spinner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../primitives/tooltip';
import { cn } from '../../utils/cn';

/**
 * UzButton - Utazzo design system Button.
 *
 * Wraps the shadcn Button primitive and adds higher-order props that show
 * up across the app: loading state, optional icons, tooltips, full-width
 * mode, and analytics attributes. Forwards everything else through.
 *
 * New props live here so we can extend them (e.g. permissions, confirm) in
 * one place without touching call sites.
 */
export interface UzButtonProps extends BaseButtonProps {
  /** Renders a spinner and disables the button while truthy. */
  loading?: boolean;
  /** Icon shown before the label. */
  leftIcon?: ReactNode;
  /** Icon shown after the label. */
  rightIcon?: ReactNode;
  /** Tooltip text. Adds aria-label fallback if button has no visible text. */
  tooltip?: string;
  /** Stretches to fill the parent's width. */
  fullWidth?: boolean;
  /** Stable id for analytics + e2e selectors. Rendered as data-analytics-id. */
  analyticsId?: string;
}

export const UzButton = forwardRef<HTMLButtonElement, UzButtonProps>(
  (
    {
      children,
      loading = false,
      disabled,
      leftIcon,
      rightIcon,
      tooltip,
      fullWidth = false,
      analyticsId,
      className,
      ...rest
    },
    ref,
  ) => {
    // Radix Slot (used by Button's `asChild`) requires exactly one React
    // element child. Our spinner/icon wrappers would smuggle extra siblings
    // into the slot and trip `React.Children.only`, so when asChild is on we
    // pass the consumer's element through untouched and drop loading/icon
    // decoration (callers using asChild are responsible for their own layout).
    const asChild = (rest as { asChild?: boolean }).asChild === true;
    const button = (
      <Button
        ref={ref}
        disabled={disabled ?? loading}
        aria-busy={loading || undefined}
        data-analytics-id={analyticsId}
        className={cn(fullWidth && 'w-full', className)}
        {...rest}
      >
        {asChild ? (
          children
        ) : (
          <>
            {loading && <Spinner className="mr-2 h-4 w-4" />}
            {!loading && leftIcon && <span className="mr-2 inline-flex">{leftIcon}</span>}
            {children}
            {!loading && rightIcon && <span className="ml-2 inline-flex">{rightIcon}</span>}
          </>
        )}
      </Button>
    );

    if (!tooltip) return button;

    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  },
);
UzButton.displayName = 'UzButton';
